/* iter-p0-security · Trust & Safety marketing page.
 *
 * Public route `/trust` — describes how Crown & Rose keeps the
 * platform safe. Written for prospective readers/authors, not for
 * engineers. Every claim on this page corresponds to a live
 * production feature so it stays honest.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Ban, Flag, Inbox, Link2, Users, Mail, Scale,
} from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useHreflang } from '@/lib/useHreflang';
import PublicNavBar from '@/components/landing/PublicNavBar';
import StructuredData, { breadcrumbLd, webPageLd } from '@/components/seo/StructuredData';

const SEO_FALLBACK = {
  title:       'Trust & Safety — Crown & Rose',
  description: 'How Crown & Rose keeps readers and authors safe: account suspension, abuse reporting, malicious-link scanning, anomaly monitoring, and fair appeals — explained plainly.',
  keywords:    'crown and rose trust and safety, platform abuse reporting, account suspension policy, indie bookstore safety',
};

const items = [
  {
    icon: Ban,
    title: 'Suspended accounts stay suspended',
    body: 'Our platform can lock a bad-actor account in one click. Sessions revoke instantly and the account cannot send email, publish, or complete checkout while under suspension. High-severity actions require two administrators to sign off.',
  },
  {
    icon: Flag,
    title: 'Anyone can report — and it lands with a human',
    body: 'Every book listing, author profile, and reader review has a report path. Reports are rate-limited to stop spam, deduped by target, and Slack-pinged to our on-call admin. You can also email us at abuse@crownnrose.com and it lands in a dedicated queue.',
  },
  {
    icon: Link2,
    title: 'Outbound email is scanned for malicious links',
    body: 'Every email leaving our platform is checked against Google Safe Browsing. Three flagged links from one account triggers automatic suspension — and that account can no longer harm our sender reputation or your inbox.',
  },
  {
    icon: Inbox,
    title: 'We watch for volume anomalies in real time',
    body: 'An hourly cron compares today\'s outbound send count against the trailing 7-day baseline. Anything above three standard deviations pages our team. A compromised account cannot silently blast our subscribers.',
  },
  {
    icon: Mail,
    title: 'Suspended users get a fair appeal',
    body: 'The moment we suspend an account we email the user the exact reason and a one-click appeal link. Human review completes within two business days. Automated blocks are reversed by default when the appeal has merit.',
  },
  {
    icon: Scale,
    title: 'Two-person review for permanent action',
    body: 'A permanent suspension cannot be issued by one administrator alone. A second admin must confirm within 24 hours; otherwise the account stays on a reversible suspend so nothing is lost by mistake.',
  },
  {
    icon: Users,
    title: 'We isolate outbound from inbound',
    body: 'Our transactional email lives on a dedicated subdomain (send.crownnrose.com) with its own DKIM, SPF, and DMARC. Even if a service tries to abuse our platform, it cannot damage the reputation of the main crownnrose.com inbox.',
  },
];

export default function TrustPage() {
  useSeoMeta('/trust', SEO_FALLBACK);
  useHreflang('trust');
  const navigate  = useNavigate();
  const siteUrl   = typeof window !== 'undefined' ? window.location.origin : 'https://crownnrose.com';
  const crumbs    = breadcrumbLd([
    { name: 'Home',  item: `${siteUrl}/` },
    { name: 'Trust & Safety', item: `${siteUrl}/trust` },
  ]);
  const wpLd      = webPageLd({
    siteUrl,
    path:        '/trust',
    name:        'Trust & Safety — Crown & Rose',
    description: SEO_FALLBACK.description,
  });
  return (
    <div className="min-h-screen bg-[#f8f5ee] text-[#2a1f18]" data-testid="trust-page">
      <StructuredData id="trust-breadcrumb" data={crumbs} />
      <StructuredData id="trust-page"       data={wpLd}   />
      <PublicNavBar
        onSignIn={() => navigate('/login')}
        onSignUp={() => navigate('/login?mode=signup')}
      />

      <section className="mx-auto max-w-3xl px-6 pt-12 pb-4">
        <h1 className="font-serif text-4xl leading-tight text-[#2a1f18] sm:text-5xl">
          A calm, well-lit place to read and to write.
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-[#4a3a28]">
          Crown &amp; Rose is a small operation — that means every abuse
          signal reaches a human quickly, and every legitimate account
          gets treated with respect. Here's exactly what we do to keep
          this platform trustworthy for readers, authors, and the
          general internet.
        </p>
      </section>

      <section className="mx-auto grid max-w-3xl gap-4 px-6 pb-16 pt-6 sm:grid-cols-2">
        {items.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="rounded-lg border border-[#e7d8b1] bg-[#fff8ec] p-5 shadow-sm"
            data-testid={`trust-item-${title.slice(0, 20).replace(/\W+/g, '-').toLowerCase()}`}
          >
            <Icon className="h-6 w-6 text-[#5a1c1f]" />
            <div className="mt-3 font-serif text-lg text-[#2a1f18]">{title}</div>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-[#4a3a28]">
              {body}
            </p>
          </div>
        ))}
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-16">
        <div className="rounded-lg border border-[#c6a664] bg-[#fdf6e4] p-6">
          <h2 className="font-serif text-2xl text-[#2a1f18]">Report or appeal</h2>
          <p className="mt-2 text-[13.5px] leading-relaxed text-[#4a3a28]">
            See something wrong? Email <b>abuse@crownnrose.com</b> or use
            the report link on any book, author, or review page. If your
            own account was suspended and you believe it's a mistake,
            <Link to="/appeal" className="ml-1 underline decoration-[#5a1c1f] underline-offset-2 hover:text-[#5a1c1f]" data-testid="trust-appeal-link">
              file an appeal here
            </Link>
            .
          </p>
        </div>
      </section>

      <footer className="border-t border-[#e7d8b1] bg-[#fdf6e4]">
        <div className="mx-auto max-w-3xl px-6 py-6 text-[11px] uppercase tracking-[0.18em] text-[#7a5b1a]">
          <Link to="/" className="hover:text-[#5a1c1f]">← Back to Crown &amp; Rose</Link>
        </div>
      </footer>
    </div>
  );
}
